import { Divider } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { CustomLoadingOverlay } from "../../ui/CustomLoadingOverlay/CustomLoadingOverLay";
import {
  PAYMENT_TYPE_HEADER,
  LIPA_BIDHAA_STEPS,
} from "../config/lipaBidhaaConfig";
import StepIndicator from "../../ui/StepIndicator/StepIndicator";
import { usePaymentFlow } from "./usePaymentFlow";
import { PaymentSummaryCard } from "../../shared/PaymentSummaryCard";
import { Step1Details } from "../steps/Step1Details";
import { Step2Review } from "../steps/Step2Review";
import { Step3Authorize } from "../steps/Step3Authorize";
import { Step4Result } from "../steps/Step4Result";

function getStepContent({
  currentStep,
  paymentType,
  control,
  handleStep1Next,
  handleStep2Back,
  handleStep2Next,
  isRequestingPayment,
  isLoadingCharges,
  walletBalance,
  isBalanceError,
  otpControl,
  handleOtpSubmit,
  otpValue,
  otpRecipient,
  handleStep3Submit,
  isAuthorizing,
  handleResendOtp,
  resendSecondsLeft,
  isResendCooldownActive,
}) {
  switch (currentStep) {
    case 1:
      return (
        <Step1Details
          paymentType={paymentType}
          control={control}
          onNext={handleStep1Next}
          isLoading={isLoadingCharges}
          walletBalance={walletBalance}
          isBalanceError={isBalanceError}
        />
      );
    case 2:
      return (
        <Step2Review
          walletBalance={walletBalance}
          onBack={handleStep2Back}
          onNext={handleStep2Next}
          isRequestingPayment={isRequestingPayment}
        />
      );
    case 3:
      return (
        <Step3Authorize
          control={otpControl}
          handleSubmit={handleOtpSubmit}
          otpValue={otpValue}
          otpRecipient={otpRecipient}
          onSubmit={handleStep3Submit}
          isAuthorizing={isAuthorizing}
          onResend={handleResendOtp}
          resendSecondsLeft={resendSecondsLeft}
          isResendCooldownActive={isResendCooldownActive}
          submitLabel="Verify & Complete Payment"
        />
      );
    default:
      return null;
  }
}

export function PaymentFlowContainer({ paymentType }) {
  const navigate = useNavigate();

  const handleExitFlow = (destination) => {
    navigate(destination);
  };

  const handleNewPayment = () => handleExitFlow("/lipa-bidhaa");
  const handleViewTransactions = () => handleExitFlow("/transaction-page");

  const {
    currentStep,
    control,
    walletBalance,
    isBalanceError,
    handleStep1Next,
    handleStep2Back,
    handleStep2Next,
    isRequestingPayment,
    summaryCardProps,
    isLoadingCharges,
    otpControl,
    handleOtpSubmit,
    otpValue,
    otpRecipient,
    handleStep3Submit,
    isAuthorizing,
    handleResendOtp,
    resendSecondsLeft,
    isResendCooldownActive,
    isPolling,
    transactionUuid,
    transactionData,
    isLoadingTransaction,
    isTransactionError,
    refetchTransaction,
    businessName,
    accountNumber,
    verifiedAlias,
    walletBalanceSnapshot,
    remarks,
    draftTransactionAmount,
    draftTransactionFee,
  } = usePaymentFlow(paymentType);

  if (currentStep === 4) {
    return (
      <Step4Result
        paymentType={paymentType}
        transactionUuid={transactionUuid}
        transactionData={transactionData}
        isLoadingTransaction={isLoadingTransaction}
        isTransactionError={isTransactionError}
        refetchTransaction={refetchTransaction}
        businessName={businessName}
        accountNumber={accountNumber}
        verifiedAlias={verifiedAlias}
        walletBalance={walletBalanceSnapshot}
        remarks={remarks}
        draftTransactionAmount={draftTransactionAmount}
        draftTransactionFee={draftTransactionFee}
        onNewPayment={handleNewPayment}
        onViewTransactions={handleViewTransactions}
      />
    );
  }

  const { title, description } = PAYMENT_TYPE_HEADER[paymentType] ?? {};
  const { title: summaryTitle, bannerMessage, summaryItems } = summaryCardProps;

  const stepContent = getStepContent({
    currentStep,
    paymentType,
    control,
    handleStep1Next,
    handleStep2Back,
    handleStep2Next,
    isRequestingPayment,
    isLoadingCharges,
    walletBalance,
    isBalanceError,
    otpControl,
    handleOtpSubmit,
    otpValue,
    otpRecipient,
    handleStep3Submit,
    isAuthorizing,
    handleResendOtp,
    resendSecondsLeft,
    isResendCooldownActive,
  });

  return (
    <div className="relative flex flex-col md:flex-row gap-6 mt-4">
      <CustomLoadingOverlay
        fullScreen
        visible={isPolling}
        overlayProps={{ blur: 1.4, backgroundOpacity: 0.15 }}
        loaderProps={{ color: "primary", type: "bars" }}
      />
      <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8">
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            {title}
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            {description}
          </p>
        </div>

        <Divider className="my-4" />

        <StepIndicator
          steps={LIPA_BIDHAA_STEPS}
          layout="horizontal"
          currentStep={currentStep}
        />

        <Divider className="my-4" />
        {stepContent}
      </div>
      {currentStep < 3 && (
        <PaymentSummaryCard
          title={summaryTitle}
          variant="info"
          descriptionColor="var(--mantine-color-primary-6)"
          bannerMessage={bannerMessage}
          summaryItems={summaryItems}
        />
      )}
    </div>
  );
}
